import React, { useEffect, useState, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useKeycloak } from '@react-keycloak/web';
import { useBillingStore } from '../store/billingStore';
import { useBilling } from '../hooks/useBilling';
import type { TimeEntry, CreateTimeEntryDto, UpdateTimeEntryDto } from '../types/billing.types';
import UtbmsCodeSelector from './UtbmsCodeSelector';
import ActivityCategorySelector from './ActivityCategorySelector';

const TimeEntryDrawer: React.FC = () => {
  const { keycloak } = useKeycloak();
  const store = useBillingStore();
  const { createTimeEntry, updateTimeEntry } = useBilling();
  const isEdit = store.drawerMode === 'edit';
  const editEntry = isEdit
    ? store.timeEntries.find((e) => e.time_entry_id === store.selectedTimeEntryId) ?? null
    : null;

  const [matterId, setMatterId] = useState('');
  const [entryDate, setEntryDate] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('');
  const [rate, setRate] = useState('');
  const [activityCategory, setActivityCategory] = useState('');
  const [utbmsCode, setUtbmsCode] = useState('');
  const [entryType, setEntryType] = useState<'hourly' | 'flat_rate'>('hourly');
  const [isNonBillable, setIsNonBillable] = useState(false);
  const [showOnInvoice, setShowOnInvoice] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editEntry) {
      setMatterId(editEntry.matter_id);
      setEntryDate(editEntry.entry_date?.split('T')[0] || '');
      setDescription(editEntry.description);
      setDuration(String(editEntry.duration));
      setRate(editEntry.rate != null ? String(editEntry.rate) : '');
      setActivityCategory(editEntry.activity_category || '');
      setUtbmsCode(editEntry.utbms_code || '');
      setEntryType(editEntry.entry_type);
      setIsNonBillable(editEntry.is_non_billable);
      setShowOnInvoice(editEntry.show_on_invoice);
      setNotes(editEntry.notes || '');
    } else {
      setMatterId('');
      setEntryDate(new Date().toISOString().split('T')[0]);
      setDescription('');
      setDuration('');
      setRate('');
      setActivityCategory('');
      setUtbmsCode('');
      setEntryType('hourly');
      setIsNonBillable(false);
      setShowOnInvoice(false);
      setNotes('');
    }
  }, [editEntry, store.drawerOpen]);

  // Auto-resolve rate from rate schedules when matter changes (create mode only)
  const rateResolvedForRef = useRef<string>('');
  useEffect(() => {
    if (isEdit || !matterId || rate) return;
    if (rateResolvedForRef.current === matterId) return;
    rateResolvedForRef.current = matterId;
    const params = new URLSearchParams({ matter_id: matterId, date: entryDate || new Date().toISOString().split('T')[0] });
    fetch(`/api/v1/billing/rate-schedules/resolve?${params}`, {
      headers: { Authorization: `Bearer ${keycloak?.token || ''}` },
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.source && data.source !== 'none' && !rate) {
          setRate(String(data.rate));
        }
      })
      .catch(() => {});
  }, [matterId, isEdit, entryDate, rate, keycloak?.token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEdit && editEntry) {
        const dto: UpdateTimeEntryDto = {
          entry_date: entryDate,
          description,
          duration: parseFloat(duration) || 0,
          rate: rate ? parseFloat(rate) : undefined,
          activity_category: activityCategory || undefined,
          utbms_code: utbmsCode || undefined,
          entry_type: entryType,
          is_non_billable: isNonBillable,
          show_on_invoice: showOnInvoice,
          notes: notes || undefined,
        };
        await updateTimeEntry(editEntry.time_entry_id, dto);
      } else {
        const dto: CreateTimeEntryDto = {
          matter_id: matterId,
          entry_date: entryDate,
          description,
          duration: parseFloat(duration) || 0,
          rate: rate ? parseFloat(rate) : undefined,
          activity_category: activityCategory || undefined,
          utbms_code: utbmsCode || undefined,
          entry_type: entryType,
          is_non_billable: isNonBillable,
          show_on_invoice: showOnInvoice,
          notes: notes || undefined,
        };
        await createTimeEntry(dto);
      }
      store.closeDrawer();
    } catch {
      // error already set in store by hook
    } finally {
      setSubmitting(false);
    }
  };

  const computedAmount =
    duration && rate ? (parseFloat(duration) * parseFloat(rate)).toFixed(2) : '-';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={store.closeDrawer}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-[420px] bg-white dark:bg-gray-900 shadow-xl z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit Time Entry' : 'New Time Entry'}
          </h2>
          <button
            onClick={store.closeDrawer}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Matter ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Matter ID
            </label>
            <input
              type="text"
              required
              value={matterId}
              onChange={(e) => setMatterId(e.target.value)}
              disabled={isEdit}
              placeholder="Enter matter UUID"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm disabled:opacity-50"
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date
            </label>
            <input
              type="date"
              required
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>

          {/* Duration & Rate */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Duration (hours)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="0.0"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Rate ($/hr)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>

          {/* Computed amount */}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Amount: <span className="font-medium text-gray-900 dark:text-white">${computedAmount}</span>
          </div>

          {/* Entry type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Entry Type
            </label>
            <select
              value={entryType}
              onChange={(e) => setEntryType(e.target.value as 'hourly' | 'flat_rate')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="hourly">Hourly</option>
              <option value="flat_rate">Flat Rate</option>
            </select>
          </div>

          {/* Activity & UTBMS */}
          <div className="grid grid-cols-2 gap-3">
            <ActivityCategorySelector
              value={activityCategory}
              onChange={setActivityCategory}
              onUtbmsCodeSuggested={(code) => { if (!utbmsCode) setUtbmsCode(code); }}
            />
            <UtbmsCodeSelector
              value={utbmsCode}
              onChange={setUtbmsCode}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              required
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe work performed..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Internal Notes
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional internal notes..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none"
            />
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={isNonBillable}
                onChange={(e) => setIsNonBillable(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              Non-billable
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={showOnInvoice}
                onChange={(e) => setShowOnInvoice(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              Show on invoice
            </label>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={store.closeDrawer}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit as any}
            disabled={submitting || !matterId || !description}
            className="flex-1 px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            {submitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </>
  );
};

export default TimeEntryDrawer;
